import React, { createContext, type ReactNode, useCallback, useContext, useMemo } from 'react';
import { type Edge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';

export type DraggableState =
	| { type: 'idle' }
	| { container: HTMLElement; type: 'preview' }
	| { type: 'dragging' };

export type ReorderSource = 'dragging' | 'reorder-dropdown';

export type ReorderDropdownAction = 'move-to-top' | 'move-up' | 'move-down' | 'move-to-bottom';

export const MAX_ITEMS_TO_SHOW = 7;

export type DraggableItemListContextValue = {
	itemListLength: number;
	numNonDraggableItems: number;
	reorderItem?: (args: {
		closestEdgeOfTarget: Edge | null;
		dropdownAction?: ReorderDropdownAction;
		indexOfTarget: number;
		source: ReorderSource;
		startIndex: number;
	}) => void;
};

export const DraggableItemListContext: React.Context<DraggableItemListContextValue> =
	createContext<DraggableItemListContextValue>({
		itemListLength: 0,
		reorderItem: undefined,
		numNonDraggableItems: 0,
	});

const DraggableItemListProvider = ({ children }: { children: ReactNode }): React.JSX.Element => {
	const reorderItem = useCallback(
		(args: {
			closestEdgeOfTarget: Edge | null;
			dropdownAction?: ReorderDropdownAction;
			indexOfTarget: number;
			source: ReorderSource;
			startIndex: number;
		}) => {},
		[],
	);

	const draggableItemListProviderState = useMemo<DraggableItemListContextValue>(
		() => ({
			itemListLength: 0,
			reorderItem,
			numNonDraggableItems: 0,
		}),
		[reorderItem],
	);

	return (
		<DraggableItemListContext.Provider value={draggableItemListProviderState}>
			{children}
		</DraggableItemListContext.Provider>
	);
};

export const useDraggableItemListProvider = (): DraggableItemListContextValue =>
	useContext(DraggableItemListContext);

export default DraggableItemListProvider;
